import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FormField } from '@/shared/components/crud/form-field'
import { Input } from '@/shared/components/ui/input'
import { Switch } from '@/shared/components/ui/switch'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { apiClient } from '@/shared/lib/api/api-client'
import { editAccountSchema, type EditAccountFormValues } from '../schemas/bank-account.schema'
import { bankAccountsApi } from '../api/bank-accounts-api'
import { AccountProvenance, type BankAccount } from '../types/bank-account'
import { isCurrencyEditable } from '../lib/currency-editable'
import { canChangeConnectivity, type ConnectivityValue } from '../lib/account-connectivity'
import { readAccountUpdateError } from '../lib/account-update-error'
import { WizardCompanyField, WizardCurrencyField, type CompanyOption, type CurrencyOption } from './wizard-account-shared-fields'
import { AccountDeactivateConfirmDialog } from './account-deactivate-confirm-dialog'
import { WizardPaymentsSection } from './wizard-payments-section'
import { useSetAccountPaymentRule } from '../lib/use-payment-rules'

interface WizardEditAccountFieldsProps {
  account: BankAccount
  // The wizard's selected connection; only saved when the account may change it (UC-094).
  connectivity: ConnectivityValue
  onDone: () => void
  onBusyChange: (busy: boolean) => void
}

const FORM_ID = 'wizard-edit-account-form'

/** Edit-mode fields, ported from the retired edit-account-dialog.tsx without the
 * DetailDialog shell (this content is embedded inside the wizard's own dialog). */
export function WizardEditAccountFields({ account, connectivity, onDone, onBusyChange }: WizardEditAccountFieldsProps) {
  const { t } = useTranslation()
  const update = bankAccountsApi.useUpdate()
  const setPaymentRule = useSetAccountPaymentRule()
  const [showDeactivateWarning, setShowDeactivateWarning] = useState(false)

  /*
   * Held outside the form because it is saved by its own route: the core gave the channel one of its
   * own, since it is the single field that decides whether money can leave, and folding it into the
   * account's PUT would make it something whoever renames an account also writes.
   *
   * Seeded from the row the grid already has, so opening the form shows what the account HAS rather
   * than an empty select that would silently clear it on save.
   */
  const [paymentRuleId, setPaymentRuleId] = useState<number | null>(account.paymentRuleId ?? null)
  const [paymentsEnabled, setPaymentsEnabled] = useState<boolean>(account.paymentsEnabled !== false)

  useEffect(() => {
    onBusyChange(update.isPending)
    return () => onBusyChange(false)
  }, [update.isPending, onBusyChange])
  const isOpenBanking = account.accountProvenance === AccountProvenance.OpenBanking
  const currencyEditable = isCurrencyEditable(account)

  const { data: companies = [] } = useQuery<CompanyOption[]>({
    queryKey: ['companies'],
    queryFn: () => apiClient.get<CompanyOption[]>('/api/companies'),
    staleTime: 10 * 60 * 1000,
  })

  const { data: currencies = [] } = useQuery<CurrencyOption[]>({
    queryKey: ['currencies'],
    queryFn: () => apiClient.get<CurrencyOption[]>('/api/Currencies'),
    staleTime: 10 * 60 * 1000,
  })

  const form = useForm<EditAccountFormValues>({
    resolver: zodResolver(editAccountSchema),
    defaultValues: {
      id: account.id,
      accountName: account.accountName ?? '',
      description: account.description ?? '',
      iban: account.iban ?? '',
      accountNumber: account.accountNumber ?? '',
      companyId: account.companyId,
      currencyId: account.currencyId ?? 0,
      active: account.active,
    },
  })

  useEffect(() => {
    // Legacy accounts may be missing currencyId; fall back to resolving it from the
    // loaded currencies list by isoCode so the disabled select still shows a value.
    if (form.getValues('currencyId')) return
    const resolvedCurrencyId = account.currencyId
      ?? currencies.find((currency) => currency.isoCode === account.currencyIsoCode)?.id
      ?? 0
    if (resolvedCurrencyId) {
      form.setValue('currencyId', resolvedCurrencyId, { shouldValidate: false, shouldDirty: false })
    }
  }, [account, currencies, form])

  async function saveAccount(values: EditAccountFormValues) {
    try {
      const { accountName, ...rest } = values
      // Left out when the connection may not change, so the PUT stays exactly what it was before CU-93.
      const connectivityChange = canChangeConnectivity(account) ? { connectivity } : {}
      await update.mutateAsync({
        id: values.id,
        data: { ...rest, name: accountName, paymentsEnabled, ...connectivityChange },
      })

      /*
       * Only when it actually changed. The PUT is cheap but not free — it re-reads the tenant's
       * catalogue to validate the rule — and an account whose channel nobody touched has no business
       * asking th_services anything.
       */
      if (paymentRuleId !== (account.paymentRuleId ?? null)) {
        try {
          await setPaymentRule.mutateAsync({ accountId: values.id, paymentRuleId })
        } catch {
          // Reported on its own: the rest of the account is already saved, and one toast saying
          // «failed to update account» over a name that did change would be a lie.
          toast.warning(t('bankAccounts.payments.saveFailed'))
        }
      }

      toast.success('Account updated')
      onDone()
    } catch (error) {
      const { code, message } = readAccountUpdateError(error)
      toast.error(t(`bankAccounts.updateErrors.${code}`, { defaultValue: message ?? 'Failed to update account' }))
    }
  }
  async function onSubmit(values: EditAccountFormValues) {
    if (currencyEditable && values.currencyId <= 0) {
      form.setError('currencyId', { message: 'Currency is required' })
      return
    }
    await saveAccount(values)
  }

  /** Switching an active account off asks first (UC-080); the switch only moves once confirmed. */
  function handleActiveChange(checked: boolean) {
    if (account.active && !checked) {
      setShowDeactivateWarning(true)
      return
    }
    form.setValue('active', checked, { shouldDirty: true })
  }

  return (
    <>
      <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField control={form.control} name="accountName" label="Account Name" required>
          {(field) => <Input {...field} />}
        </FormField>
        <FormField control={form.control} name="description" label="Description">
          {(field) => <Input {...field} />}
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="iban" label="IBAN" required>
            {(field) => <Input {...field} disabled={isOpenBanking} />}
          </FormField>
          <FormField control={form.control} name="accountNumber" label="Account Number" required>
            {(field) => <Input {...field} disabled={isOpenBanking} />}
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <WizardCompanyField control={form.control} name="companyId" companies={companies} />
          <WizardCurrencyField
            control={form.control}
            name="currencyId"
            currencies={currencies}
            disabled={!currencyEditable}
          />
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Switch
            id="active"
            checked={form.watch('active')}
            onCheckedChange={handleActiveChange}
          />
          <Label htmlFor="active">Active</Label>
        </div>

        <WizardPaymentsSection
          paymentsEnabled={paymentsEnabled}
          onPaymentsEnabledChange={setPaymentsEnabled}
          paymentRuleId={paymentRuleId}
          onPaymentRuleChange={setPaymentRuleId}
        />

        <div className="flex justify-end pt-2">
          <Button type="submit" form={FORM_ID} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>
      <AccountDeactivateConfirmDialog
        open={showDeactivateWarning}
        onOpenChange={setShowDeactivateWarning}
        onConfirm={() => {
          // Only the switch moves: the deactivation is saved with the rest of the form on "Save Changes".
          form.setValue('active', false, { shouldDirty: true })
          setShowDeactivateWarning(false)
        }}
      />
    </>
  )
}
